import { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { EmptyState } from '../ui/EmptyState';
import { SpinnerOverlay } from '../ui/Spinner';
import { cn } from '../../utils/cn';
import { useGetClassFilterPresets } from '../../features/documentation/hooks/getDropdownOptions';

interface PickClassificationPresetModalProps {
  visible?: boolean;
  onClose?: () => void;
  onSubmit?: (classifications: any[]) => void;
  value?: any[];
}

const PickClassificationPresetModal = ({
  visible,
  onClose,
  onSubmit,
  value = [],
}: PickClassificationPresetModalProps = {}) => {
  const { data: presets, isLoading } = useGetClassFilterPresets(!!visible);
  const [selected, setSelected] = useState<Set<any>>(new Set());
  const [openMenus, setOpenMenus] = useState<Set<any>>(new Set());

  const locked = useMemo(() => new Set((value ?? []).map((item: any) => item.id)), [value]);

  useEffect(() => {
    if (!visible) return;
    setSelected(new Set(locked));
  }, [visible, locked]);

  const reset = () => {
    setSelected(new Set());
    setOpenMenus(new Set());
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const toggleOpen = (id: any) => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleClassification = (id: any) => {
    if (locked.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMenu = (menu: any) => {
    const childIds: any[] = (menu.classifications ?? []).map((cls: any) => cls.id);
    const allSelected = childIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      childIds.forEach((id) => {
        if (locked.has(id)) return;
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const handleSubmit = () => {
    const merged = (presets ?? [])
      .flatMap((menu: any) => menu.classifications ?? [])
      .filter(
        (cls: any, index: number, self: any[]) =>
          selected.has(cls.id) && index === self.findIndex((o) => o.id === cls.id)
      );

    onSubmit?.(merged);
    reset();
  };

  const menuState = (menu: any) => {
    const childIds: any[] = (menu.classifications ?? []).map((cls: any) => cls.id);
    const allSelected = childIds.length > 0 && childIds.every((id) => selected.has(id));
    const someSelected = childIds.some((id) => selected.has(id));
    const allLocked = childIds.length > 0 && childIds.every((id) => locked.has(id));
    return {
      checked: allSelected ? true : someSelected ? ('indeterminate' as const) : false,
      disabled: allLocked,
    };
  };

  return (
    <Dialog open={visible} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Vyberte kategorie</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="h-[350px] overflow-auto">
            {isLoading ? (
              <SpinnerOverlay />
            ) : !presets || presets.length === 0 ? (
              <EmptyState title="Žádné dostupné presety" />
            ) : (
              <ul className="flex flex-col">
                {presets.map((menu: any) => {
                  const isOpen = openMenus.has(menu.id);
                  const state = menuState(menu);
                  return (
                    <li key={menu.id} className="border-b border-neutral-200">
                      <div className="flex items-center gap-1.5 py-2">
                        <button
                          type="button"
                          onClick={() => toggleOpen(menu.id)}
                          className="flex items-center shrink-0"
                          data-testid={`pick-classification-preset-modal-menu-toggle-${menu.id}`}
                        >
                          <ChevronRight
                            size={14}
                            className={cn(
                              'text-neutral-400 transition-transform',
                              isOpen && 'rotate-90'
                            )}
                          />
                        </button>
                        <Checkbox
                          checked={state.checked}
                          disabled={state.disabled}
                          onCheckedChange={() => toggleMenu(menu)}
                          data-testid={`pick-classification-preset-modal-menu-${menu.id}`}
                        />
                        <span
                          className="text-sm font-medium text-neutral-700 cursor-pointer"
                          onClick={() => toggleOpen(menu.id)}
                        >
                          {menu.title}
                        </span>
                      </div>
                      {isOpen && (
                        <ul className="flex flex-col">
                          {(menu.classifications ?? []).map((cls: any) => {
                            const isLocked = locked.has(cls.id);
                            return (
                              <li
                                key={cls.id}
                                className={cn(
                                  'flex items-center gap-2 py-1.5 pl-9',
                                  isLocked ? 'cursor-default' : 'cursor-pointer'
                                )}
                                onClick={() => toggleClassification(cls.id)}
                              >
                                <Checkbox
                                  size="sm"
                                  checked={selected.has(cls.id)}
                                  disabled={isLocked}
                                  data-testid={`pick-classification-preset-modal-item-${cls.id}`}
                                />
                                <span
                                  className={cn(
                                    'text-sm',
                                    isLocked ? 'text-neutral-400' : 'text-neutral-600'
                                  )}
                                >
                                  {cls.title}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            data-testid="pick-classification-preset-modal-close-btn"
            variant="outline"
            onClick={handleClose}
          >
            Zavřít
          </Button>
          <Button
            key={2}
            data-testid="pick-classification-preset-modal-confirm-btn"
            onClick={handleSubmit}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PickClassificationPresetModal;
